import React, { useState, useRef, useEffect } from 'react'
import CheckOutlined from '@ant-design/icons/CheckOutlined'
import CopyOutlined from '@ant-design/icons/CopyOutlined'
import LinkOutlined from '@ant-design/icons/LinkOutlined'

import {
  CopyableContainer,
  CopyableLink,
  CopyableText,
  CopyIcon,
  EmptyValue,
} from './CopyableValue.styles'

type CopyButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  $copied?: boolean
}

const CopyableValue = ({
  value,
  href,
  emptyLabel = '-',
  showTitle = true,
  iconOnly = false,
  iconVariant = 'copy',
  IconButton = CopyIcon,
}: {
  value?: string | null
  href?: string
  emptyLabel?: string
  showTitle?: boolean
  iconOnly?: boolean
  iconVariant?: 'copy' | 'link'
  IconButton?: React.ComponentType<CopyButtonProps>
}) => {
  const displayValue = value ?? href
  const [copied, setCopied] = useState(false)
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (resetTimeoutRef.current !== null) {
        clearTimeout(resetTimeoutRef.current)
      }
    }
  }, [])

  const handleCopy = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (!displayValue) return

    try {
      await navigator.clipboard.writeText(displayValue)
      if (resetTimeoutRef.current !== null) {
        clearTimeout(resetTimeoutRef.current)
      }
      setCopied(true)
      resetTimeoutRef.current = setTimeout(() => {
        resetTimeoutRef.current = null
        setCopied(false)
      }, 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  if (!displayValue) {
    return iconOnly ? null : <EmptyValue>{emptyLabel}</EmptyValue>
  }

  if (iconOnly) {
    const CopyIconGlyph = iconVariant === 'link' ? LinkOutlined : CopyOutlined

    return (
      <IconButton
        type="button"
        $copied={copied}
        onClick={handleCopy}
        aria-label={
          copied
            ? `Copied ${displayValue}`
            : `Copy ${displayValue} to clipboard`
        }
      >
        {copied ? (
          <CheckOutlined aria-hidden="true" />
        ) : (
          <CopyIconGlyph aria-hidden="true" />
        )}
      </IconButton>
    )
  }

  const title = showTitle ? displayValue : undefined

  return (
    <CopyableContainer>
      {href ? (
        <CopyableLink
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          title={title}
        >
          {displayValue}
        </CopyableLink>
      ) : (
        <CopyableText title={title}>{displayValue}</CopyableText>
      )}
      <CopyIcon
        type="button"
        $copied={copied}
        onClick={handleCopy}
        aria-label={
          copied
            ? `Copied ${displayValue}`
            : `Copy ${displayValue} to clipboard`
        }
      >
        {copied ? (
          <CheckOutlined aria-hidden="true" />
        ) : (
          <CopyOutlined aria-hidden="true" />
        )}
      </CopyIcon>
      <span
        aria-live="polite"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {copied ? 'Copied to clipboard' : ''}
      </span>
    </CopyableContainer>
  )
}

export { CopyableValue }
